import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-common';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LabpartnerService } from './labpartner.service';
import { User, UserAccess } from './labpartner.service.model';

type TokenClaims = {
  roles: string[];
};

export const LP_ROLE_NAMES = {
  ADMIN: 'LabPartner.Administrators',
  WRITER: 'LabPartner.Writers',
  READER: 'LabPartner.Readers',
  CLINICALTRIAL: 'ResultsBundler.ClinicalTrials',
  VIEWER: 'LabPartner.Viewers',
};

export const IC_ROLE_NAMES = {
  ICWRITER: 'LabPartner.IC.Writers',
};

export const ROLE_NAMES = {
  ...LP_ROLE_NAMES,
  ...IC_ROLE_NAMES,
};

@Injectable({
  providedIn: 'root',
})
export class UserAccountService {
  private user!: User;
  private users: User[] = [];
  private roles: string[] = [];

  private currentRoles = new BehaviorSubject<string[]>([]);
  currentRoles$ = this.currentRoles.asObservable();

  private currentUser = new BehaviorSubject<User | undefined>(undefined);
  currentUser$ = this.currentUser.asObservable();

  constructor(private apiService: LabpartnerService) {
    if (environment.testMode) {
      (window as any)['UserAccountService'] = this;
    }

    this.clearUser();
  }

  public async SetUserList(hideLoader?: boolean) {
    const response = await this.apiService.getUsersList(hideLoader).toPromise();

    if (!response) {
      return;
    }

    const sortedUsers = response.sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });

    this.users = sortedUsers;
  }

  public clearUser() {
    this.user = {
      userId: 0,
      displayName: '',
      accountId: '',
      email: '',
    };

    this.roles = [];

    // Notify subscribers that user and roles have been cleared
    this.currentUser.next(this.user);
    this.currentRoles.next(this.roles);
  }

  public setUser(acct: AccountInfo) {
    // Check if we're in test mode
    const isTestMode = environment.testMode;

    if (isTestMode) {
      // In test mode, use the same flow as normal mode
      // Call the API to get/create the user in the database (like in normal mode)
      this.checkAddUser(acct);

      // Set user properties (like in normal mode)
      this.user.accountId = acct.localAccountId;
      this.user.displayName = acct.name!;
      this.user.email = acct.username;
      this.user.userId = parseInt(acct.localAccountId.replace(/\D/g, '').slice(0, 8)) || 1;

      const claims = acct.idTokenClaims as TokenClaims;
      this.roles = claims.roles;

      // Set logged in user in the API service (like in normal mode)
      this.apiService.setLoggedInUser(this.user.userId);

      // Load user list in test mode as well
      this.SetUserList(true).catch(error => {
        console.error('[UserAccountService] Error loading user list in test mode:', error);
      });

      // Notify subscribers after everything is set up (like in normal mode)
      this.currentUser.next(this.user);
      this.currentRoles.next(this.roles);
    } else {
      // Normal mode - call API to get user
      this.checkAddUser(acct);
      this.user.accountId = acct.localAccountId;
      this.user.displayName = acct.name!;
      this.user.email = acct.username;

      const claims = acct.idTokenClaims as TokenClaims;
      this.roles = claims.roles;

      console.log(
        '[UserAccountService] Setting user from account info: ' + acct.localAccountId,
        this.roles,
      );
      this.currentRoles.next(this.roles);
    }

    //TODO: fire event that this info has changed
    // but these current properties are not going to change in the lifetime of a login
  }

  checkAddUser(acct: AccountInfo) {
    this.apiService.getUserByAccountId(acct.localAccountId).subscribe(
      (result: UserAccess) => {
        this.user = result;
        if (this.user != null) {
          this.apiService.setLoggedInUser(this.user.userId);
          console.log('User found Name:' + this.user.displayName + ' id:' + this.user.userId);
          this.checkUpdateUser(this.user, acct);
        } else {
          console.log('Failed to get user: returned null');
          this.createUser(acct);
        }
        this.currentUser.next(result);
      },
      err => {
        console.log('Failed to get user: ' + err);
        //Create the user
        this.createUser(acct);
      },
    );
  }

  private checkUpdateUser(user: User, acct: AccountInfo) {
    if (user.displayName != acct.name! || user.email != acct.username) {
      user.displayName = acct.name!;
      user.email = acct.username;
      this.apiService
        .updateUser(user)
        .pipe(
          finalize(() => {
            this.currentUser.next(this.user);
          }),
        )
        .subscribe(
          () => {
            this.user = user;
          },
          err => console.log('Error updating user err:' + err),
        );
    }
  }

  private createUser(acct: AccountInfo) {
    const user: User = {
      userId: 0,
      accountId: acct.localAccountId,
      displayName: acct.name!,
      email: acct.username,
    };

    this.apiService
      .createUser(user)
      .pipe(
        finalize(() => {
          this.currentUser.next(this.user);
        }),
      )
      .subscribe(
        (result: User) => {
          this.user = result;
          this.apiService.setLoggedInUser(this.user.userId);
        },
        err => console.log('Error creating user err:' + err),
      );
  }

  getUserName(userId: number): string {
    const found = this.users.find(u => u.userId == userId);
    if (found != null) {
      return found.displayName;
    }
    return 'Unknown User';
  }

  public removeAdmin() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.ADMIN);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public removeWriter() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.WRITER);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public removeClinicalTrial() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.CLINICALTRIAL);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public removeReader() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.READER);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public removeViewer() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.VIEWER);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public removeICWriter() {
    const roles = this.roles.filter(r => r != ROLE_NAMES.ICWRITER);
    this.roles = roles;
    this.currentRoles.next(roles);
  }

  public async WaitUntilWeHaveUser(): Promise<boolean> {
    const promise = new Promise<boolean>(resolve => {
      const timer = setInterval(() => {
        if (this.user != null) {
          clearInterval(timer);
          resolve(true);
        }
      }, 250);
    });

    return promise;
  }

  public getUser(): User {
    return this.user;
  }

  public getUsers(): User[] {
    return this.users;
  }

  public getRoles(): string[] {
    return this.roles;
  }

  public hasValidRole() {
    if (!this.roles || this.roles.length === 0) {
      return false;
    }

    for (const role of Object.values(ROLE_NAMES)) {
      if (this.roles.includes(role)) {
        return true;
      }
    }
    return false;
  }

  public isUserInRole(roleName: string): boolean {
    if (!this.roles) {
      return false;
    }
    return this.roles.indexOf(roleName) != -1;
  }

  public isAdmin(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.ADMIN);
  }

  public isWriter(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.WRITER);
  }

  public isReader(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.READER);
  }

  public isViewer(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.VIEWER);
  }

  public isClinicalTrial(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.CLINICALTRIAL);
  }

  public isICWriter(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isUserInRole(ROLE_NAMES.ICWRITER);
  }

  public isICUser(): boolean {
    if (!this.roles) {
      return false;
    }
    return this.isICWriter();
  }

  public isLPUser(): boolean {
    if (!this.roles) {
      return false;
    }
    return (
      this.isAdmin() ||
      this.isWriter() ||
      this.isReader() ||
      this.isViewer() ||
      this.isClinicalTrial()
    );
  }

  public canEditBundle(userId: number): boolean {
    if (this.user.userId == userId) {
      return true;
    }
    return false;
  }

  public canDeleteBundle(userId: number): boolean {
    if (this.user.userId === userId) {
      return true;
    }
    return false;
  }

  public canEditClinicalTrial(): boolean {
    return this.isClinicalTrial();
  }
}
